import {
  DisplayOrientation,
  ScreenName,
  type DisplaySlot,
  type WindowLayout,
  type WorkspaceLayoutPreset,
  WindowType,
  WindowState,
} from 'generated/graphql';
import { getScreenName } from 'utils/screenNames';
import { logger } from 'modules/logger';
import type {
  WorkspaceLayoutSnapshot,
  WorkspaceDisplay,
  WorkspaceWindow,
  Point,
  Rect,
  RelativeRect,
} from './types';

export type IndexedDisplay = {
  index: number;
  display: WorkspaceDisplay;
};

export const buildWorkspaceLayoutPreset = (
  snapshot: WorkspaceLayoutSnapshot
): Partial<WorkspaceLayoutPreset> => {
  const displays = getIndexedDisplays(snapshot.displays);

  if (displays.length === 0) {
    logger.error(
      '[WorkspaceLayoutManager] Cannot capture workspace layout without at least one display',
      snapshot
    );
  }

  const windows = buildWindowLayouts(snapshot.windows, displays);
  const timestamp = new Date();

  return {
    displayArrangementKey: buildDisplayArrangementKey(displays),

    displaySlots: buildDisplaySlots(displays),
    windows,

    createdAt: timestamp,
    updatedAt: timestamp,
  };
};

export const getIndexedDisplays = (displays: WorkspaceDisplay[]): IndexedDisplay[] => {
  return [...displays].sort(compareDisplaysForStableIndexing).map((display, index) => ({
    index,
    display,
  }));
};

const compareDisplaysForStableIndexing = (a: WorkspaceDisplay, b: WorkspaceDisplay): number => {
  if (a.isPrimary && !b.isPrimary) return -1;
  if (!a.isPrimary && b.isPrimary) return 1;

  return (
    a.bounds.left - b.bounds.left ||
    a.bounds.top - b.bounds.top ||
    a.bounds.width - b.bounds.width ||
    a.bounds.height - b.bounds.height
  );
};

export const buildDisplaySlots = (displays: IndexedDisplay[]): Array<DisplaySlot> => {
  return displays.map(({ index, display }) => ({
    __typename: 'DisplaySlot' as const,
    index,
    isPrimary: display.isPrimary,
    orientation: getDisplayOrientation(display),
    width: display.bounds.width,
    height: display.bounds.height,
    left: display.bounds.left,
    top: display.bounds.top,
  }));
};

export const buildDisplayArrangementKeyFromDisplaySlots = (displaySlots: DisplaySlot[]): string => {
  const sortedSlots = [...displaySlots].sort((a, b) => {
    const topDiff = a.top - b.top;
    if (topDiff !== 0) return topDiff;

    return a.left - b.left;
  });

  const rows: DisplaySlot[][] = [];

  sortedSlots.forEach((slot) => {
    const existingRow = rows.find((row) => Math.abs(slot.top - row[0].top) < 50);

    if (existingRow) {
      existingRow.push(slot);
    } else {
      rows.push([slot]);
    }
  });

  return rows
    .map((row) =>
      row
        .sort((a, b) => a.left - b.left)
        .map((slot) => slot.orientation)
        .join(',')
    )
    .join('/');
};

export const buildDisplayArrangementKey = (displays: IndexedDisplay[]): string => {
  const sortedDisplays = [...displays].sort((a, b) => {
    const topDiff = a.display.bounds.top - b.display.bounds.top;
    if (topDiff !== 0) return topDiff;

    return a.display.bounds.left - b.display.bounds.left;
  });

  const rows: IndexedDisplay[][] = [];

  sortedDisplays.forEach((indexedDisplay) => {
    const existingRow = rows.find((row) => {
      const rowTop = row[0].display.bounds.top;
      return Math.abs(indexedDisplay.display.bounds.top - rowTop) < 50;
    });

    if (existingRow) {
      existingRow.push(indexedDisplay);
    } else {
      rows.push([indexedDisplay]);
    }
  });

  return rows
    .map((row) =>
      row
        .sort((a, b) => a.display.bounds.left - b.display.bounds.left)
        .map(({ display }) => getDisplayOrientation(display))
        .join(',')
    )
    .join('/');
};

export const buildWindowLayouts = (
  windows: WorkspaceWindow[],
  displays: IndexedDisplay[]
): Array<WindowLayout> => {
  if (displays.length === 0) {
    return [];
  }

  return windows.filter(shouldCaptureWindow).map((windowInfo) => {
    const display = getDisplayForWindow(windowInfo, displays);
    const bounds = display.display.workArea ?? display.display.bounds;
    const firstTab = windowInfo.tabs[0];

    return {
      __typename: 'WindowLayout' as const,
      id: String(windowInfo.id),
      type: windowInfo.type ?? WindowType.Popup,
      state: windowInfo.state ?? WindowState.Normal,
      screen: mapScreenName(firstTab?.url),
      displayIndex: display.index,
      relX: toRelative((windowInfo.left - bounds.left) / bounds.width),
      relY: toRelative((windowInfo.top - bounds.top) / bounds.height),
      relW: toRelative(windowInfo.width / bounds.width),
      relH: toRelative(windowInfo.height / bounds.height),
    };
  });
};

const shouldCaptureWindow = (windowInfo: WorkspaceWindow): boolean => {
  if (windowInfo.width <= 0 || windowInfo.height <= 0) return false;
  if (!Array.isArray(windowInfo.tabs) || windowInfo.tabs.length === 0) return false;

  return true;
};

export const getDisplayOrientation = (display: WorkspaceDisplay): DisplayOrientation => {
  const { width, height } = display.bounds;

  if (display.rotation === 90 || display.rotation === 270) {
    return width >= height ? DisplayOrientation.Portrait : DisplayOrientation.Landscape;
  }

  return width >= height ? DisplayOrientation.Landscape : DisplayOrientation.Portrait;
};

export const getDisplayForWindow = (
  windowInfo: WorkspaceWindow,
  displays: IndexedDisplay[]
): IndexedDisplay => {
  const center = getWindowCenter(windowInfo);

  const containingDisplay = displays.find(({ display }) => pointInBounds(center, display.bounds));

  if (containingDisplay) return containingDisplay;

  return displays.reduce((closest, candidate) => {
    const closestDistance = distanceToBounds(center, closest.display.bounds);
    const candidateDistance = distanceToBounds(center, candidate.display.bounds);

    return candidateDistance < closestDistance ? candidate : closest;
  }, displays[0]);
};

export const getWindowCenter = (windowInfo: WorkspaceWindow): Point => {
  return {
    x: windowInfo.left + windowInfo.width / 2,
    y: windowInfo.top + windowInfo.height / 2,
  };
};

export const pointInBounds = (point: Point, bounds: Rect): boolean => {
  return (
    point.x >= bounds.left &&
    point.x < bounds.left + bounds.width &&
    point.y >= bounds.top &&
    point.y < bounds.top + bounds.height
  );
};

export const distanceToBounds = (point: Point, bounds: Rect): number => {
  const clampedX = Math.max(bounds.left, Math.min(point.x, bounds.left + bounds.width));
  const clampedY = Math.max(bounds.top, Math.min(point.y, bounds.top + bounds.height));

  return Math.hypot(point.x - clampedX, point.y - clampedY);
};

export const toRelative = (value: number): number => {
  if (!Number.isFinite(value)) return 0;
  return Number(value.toFixed(6));
};

const mapScreenName = (url?: string | null): ScreenName => {
  if (!url) return ScreenName.Worklist;

  try {
    return getScreenName(url);
  } catch {
    logger.warn(`[WorkspaceLayoutManager] Unknown screen for URL ${url}; defaulting to WORKLIST`);
    return ScreenName.Worklist;
  }
};

export const resolveRelativeRect = (container: Rect, relative: RelativeRect): Rect => ({
  left: container.left + relative.relX * container.width,
  top: container.top + relative.relY * container.height,
  width: relative.relW * container.width,
  height: relative.relH * container.height,
});

export enum CompatibilityResult {
  Exact = 'exact',
  Compatible = 'compatible',
  Incompatible = 'incompatible',
}

/**
 *
 * @param presetDisplays - the display slots of the workspace layout we are considering
 * @param currentDisplays - the display slots of the current workstation
 * @returns The compatibility between the workspace layout preset and the current workstation display arrangement. Incompatible means the preset cannot be applied, while Compatible means it can be applied and will result in the expected display arrangement. Note that Compatible does not necessarily mean that the preset matches the current display arrangement, as the preset could still be applied to achieve the expected arrangement.
 *
 * If the preset has more display slots than the current display arrangement, then it's incompatible. Otherwise, we check if every preset slot matches the corresponding current slot in terms of orientation. If all orientations match, then we consider the preset compatible, as it can be applied without changing the orientation of any display. If any orientation does not match, then we consider the preset incompatible, as applying it would require changing the orientation of at least one display, which could lead to an unexpected arrangement.
 */
export const getWorkspaceLayoutPresetCompatibility = (
  presetDisplays: DisplaySlot[],
  currentDisplays: DisplaySlot[]
): CompatibilityResult => {
  if (presetDisplays.length > currentDisplays.length) {
    return CompatibilityResult.Incompatible;
  }

  if (
    buildDisplayArrangementKeyFromDisplaySlots(presetDisplays) ===
    buildDisplayArrangementKeyFromDisplaySlots(currentDisplays)
  ) {
    return CompatibilityResult.Exact;
  }

  // Compatible means every preset slot matches the current slot at the same index
  const allSlotsMatch = presetDisplays.every(
    (preset, i) => preset.orientation === currentDisplays[i].orientation
  );

  if (!allSlotsMatch) {
    return CompatibilityResult.Incompatible;
  }

  return CompatibilityResult.Compatible;
};

export const sortWorkspaceLayoutPresets = (
  presets: Partial<WorkspaceLayoutPreset>[],
  currentDisplays: DisplaySlot[]
): Partial<WorkspaceLayoutPreset>[] => {
  return [...presets].sort((a, b) => {
    const compatA = getWorkspaceLayoutPresetCompatibility(
      [...(a.displaySlots ?? [])],
      currentDisplays
    );
    const compatB = getWorkspaceLayoutPresetCompatibility(
      [...(b.displaySlots ?? [])],
      currentDisplays
    );

    const isDefaultA = a.isDefault && compatA === CompatibilityResult.Exact;
    const isDefaultB = b.isDefault && compatB === CompatibilityResult.Exact;

    if (isDefaultA) return -1;
    if (isDefaultB) return 1;

    const compatOrder = { exact: 0, compatible: 1, incompatible: 2 };
    const compatDiff = compatOrder[compatA] - compatOrder[compatB];
    if (compatDiff !== 0) return compatDiff;

    return new Date(String(b.updatedAt)).getTime() - new Date(String(a.updatedAt)).getTime();
  });
};
